import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import {
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import LanguageIcon from '@mui/icons-material/Language'
import NotificationsIcon from '@mui/icons-material/Notifications'
import StraightenIcon from '@mui/icons-material/Straighten'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import InfoIcon from '@mui/icons-material/Info'
import MicIcon from '@mui/icons-material/Mic'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'

type Language = 'en' | 'es' | 'he'

// ─── Recording Settings Types ─────────────────────────────────────────────────

/** A hive answer repeats for every hive opened; a site answer is asked once a visit. */
type QuestionScope = 'hive' | 'yard'

interface RecordingQuestion {
  id: string
  label: string
  prompt: string
  field: string
  scope?: QuestionScope
  type: 'number' | 'boolean' | 'text' | 'select'
  options?: string[]
  enabled: boolean
  order: number
  required: boolean
  rating?: {
    high:   { label: string; color: string; min: number }
    medium: { label: string; color: string; min: number }
    low:    { label: string; color: string; min: number }
  }
}

interface HiveType {
  id: string
  label: string
  frames: number
}

interface StrengthRating {
  strong: { min: number; label: string; color: string }
  medium: { min: number; label: string; color: string }
  weak:   { min: number; label: string; color: string }
}

type RecordingMode = 'questions' | 'free'

/** Voices the Realtime API offers; marin/cedar are the clearest on Hebrew. */
const VOICES = ['marin', 'cedar', 'sage', 'verse', 'ballad', 'coral', 'shimmer', 'ash', 'echo', 'alloy'] as const

interface RecordingSettings {
  mode?: RecordingMode
  voice?: string
  questions: RecordingQuestion[]
  hiveTypes: HiveType[]
  strengthRating: StrengthRating
}

// ─── Recording Styled ─────────────────────────────────────────────────────────

const RecSection = styled.div`margin-top: 8px;`
const RecCard = styled.div`background: #fff; border-radius: 12px; padding: 20px; border: 1px solid #e5e7eb; margin-bottom: 16px;`
const RecCardTitle = styled.h2`font-size: 15px; font-weight: 700; margin-bottom: 12px; color: #111827; display: flex; align-items: center; gap: 8px;`

const QuestionRow = styled.div<{ enabled?: boolean }>`
  display: flex; align-items: center; gap: 12px; padding: 10px 12px;
  background: ${p => p.enabled ? '#fff' : '#f9fafb'};
  border: 1px solid ${p => p.enabled ? '#e5e7eb' : '#f3f4f6'};
  border-radius: 10px; margin-bottom: 8px;
  opacity: ${p => p.enabled ? 1 : 0.55};
  transition: all 0.15s;
  cursor: grab;
  &:active { cursor: grabbing; }
`

const DragHandle = styled.div`color: #d1d5db; font-size: 18px; flex-shrink: 0; user-select: none;`

const Toggle = styled.label`
  position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0;
  input { opacity: 0; width: 0; height: 0; }
  span {
    position: absolute; inset: 0; background: #d1d5db; border-radius: 22px; cursor: pointer; transition: 0.2s;
    &:before { content: ''; position: absolute; width: 16px; height: 16px; left: 3px; bottom: 3px; background: #fff; border-radius: 50%; transition: 0.2s; }
  }
  input:checked + span { background: #6366f1; }
  input:checked + span:before { transform: translateX(18px); }
  input:disabled + span { opacity: 0.5; cursor: not-allowed; }
`

const QInfo = styled.div`flex: 1; min-width: 0;`
const QLabel = styled.div`font-size: 13px; font-weight: 600; color: #111827; display: flex; align-items: center; gap: 6px;`
const QType = styled.span`font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 20px; background: #ede9fe; color: #5b21b6;`
const QPromptInput = styled.input`
  margin-top: 5px; width: 100%; padding: 5px 9px; border-radius: 6px;
  border: 1px solid #e5e7eb; font-size: 12px; outline: none; color: #374151;
  &:focus { border-color: #6366f1; }
`
const QPromptText = styled.div`font-size: 12px; color: #9ca3af; margin-top: 3px;`

const ColorDot = styled.div<{ color: string }>`
  width: 10px; height: 10px; border-radius: 50%; background: ${p => p.color}; display: inline-block; margin-right: 5px; flex-shrink: 0;
`

const SmInput = styled.input`
  width: 100%; padding: 7px 10px; border-radius: 7px; border: 1px solid #d1d5db;
  font-size: 13px; outline: none;
  &:focus { border-color: #6366f1; }
`
const SmLabel = styled.label`font-size: 12px; font-weight: 600; color: #6b7280; display: block; margin-bottom: 3px; margin-top: 10px;`

const SaveBar = styled.div`
  position: sticky; bottom: 0; background: #f9fafb; border-top: 1px solid #e5e7eb;
  padding: 14px 0; display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px;
`
const Btn = styled.button<{ variant?: string }>`
  padding: 9px 18px; border-radius: 8px; border: none; cursor: pointer; font-size: 13px; font-weight: 600;
  background: ${p => p.variant === 'ghost' ? '#f3f4f6' : '#6366f1'};
  color: ${p => p.variant === 'ghost' ? '#374151' : '#fff'};
  &:hover { opacity: 0.85; } &:disabled { opacity: 0.5; }
`

const PromptPreview = styled.pre`
  background: #1e1b4b; color: #a5b4fc; padding: 14px; border-radius: 10px;
  font-size: 11px; white-space: pre-wrap; max-height: 360px; overflow-y: auto; margin-top: 8px;
`

const TypeIcon: Record<string, string> = { number: '🔢', boolean: '✅', text: '💬', select: '📋' }

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TabsRow = styled.div`display: flex; gap: 4px; border-bottom: 2px solid #e5e7eb; margin-bottom: 24px;`
const TabBtn = styled.button<{ active?: boolean }>`
  padding: 10px 18px; border: none; background: none; cursor: pointer; font-size: 14px; font-weight: 600;
  color: ${p => p.active ? '#6366f1' : '#6b7280'};
  border-bottom: 2px solid ${p => p.active ? '#6366f1' : 'transparent'};
  margin-bottom: -2px; display: flex; align-items: center; gap: 6px;
  &:hover { color: #6366f1; }
`

// ─── Main Component ───────────────────────────────────────────────────────────

const LANG_NAME_KEY: Record<string, string> = { en: 'english', es: 'spanish', he: 'hebrew', ar: 'arabic', th: 'thai' }

function SettingsScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { language, notificationsEnabled, temperatureUnit, distanceUnit,
    setLanguage, toggleNotifications, setTemperatureUnit, setDistanceUnit } = useSettings()

  const [activeTab, setActiveTab] = useState<'general' | 'recording'>('general')

  // Recording settings state
  const [recSettings, setRecSettings] = useState<RecordingSettings | null>(null)
  const [recLoading, setRecLoading] = useState(false)
  const [recSaving, setRecSaving] = useState(false)
  const [recSaved, setRecSaved] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [dragging, setDragging] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)

  // Auto-translation of the questions into the viewer's language (display only)
  const [recTranslation, setRecTranslation] = useState<any>(null)
  const [showOriginal, setShowOriginal] = useState(false)
  const [translatingRec, setTranslatingRec] = useState(false)

  async function fetchRecTranslation() {
    setTranslatingRec(true)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await axios.post(`${API}/settings/recording/translate`, { target: language }, { headers: { Authorization: `Bearer ${token}` } })
      setRecTranslation(res.data)
    } catch (e) { console.error(e) } finally { setTranslatingRec(false) }
  }

  const isTranslated = !!recTranslation && !recTranslation.sameLanguage && !showOriginal
  const trQuestion = (id: string) => (isTranslated && recTranslation?.questions ? recTranslation.questions.find((x: any) => x.id === id) : null)

  useEffect(() => {
    if (activeTab === 'recording' && !recSettings) loadRecSettings()
  }, [activeTab]) // eslint-disable-line

  // Auto-translate the questions whenever settings load or the language changes
  useEffect(() => {
    if (activeTab === 'recording' && recSettings) {
      setShowOriginal(false)
      fetchRecTranslation()
    }
  }, [recSettings, language]) // eslint-disable-line

  async function loadRecSettings() {
    setRecLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await axios.get(`${API}/settings/recording`, { headers: { Authorization: `Bearer ${token}` } })
      setRecSettings(res.data)
    } catch (e) { console.error(e) }
    finally { setRecLoading(false) }
  }

  async function saveRecSettings() {
    if (!recSettings) return
    setRecSaving(true)
    try {
      const token = localStorage.getItem('auth_token')
      await axios.put(`${API}/settings/recording`, recSettings, { headers: { Authorization: `Bearer ${token}` } })
      setRecSaved(true)
      setTimeout(() => setRecSaved(false), 3000)
    } catch (e) { console.error(e) }
    finally { setRecSaving(false) }
  }

  async function loadPromptPreview() {
    try {
      const token = localStorage.getItem('auth_token')
      const res = await axios.get(`${API}/settings/recording/prompt`, { headers: { Authorization: `Bearer ${token}` } })
      setPrompt(res.data.prompt)
      setShowPrompt(true)
    } catch (e) { console.error(e) }
  }

  function toggleQuestion(id: string) {
    setRecSettings(s => s ? { ...s, questions: s.questions.map(q => q.id === id ? { ...q, enabled: !q.enabled } : q) } : s)
  }

  function updateQuestionPrompt(id: string, value: string) {
    setRecSettings(s => s ? { ...s, questions: s.questions.map(q => q.id === id ? { ...q, prompt: value } : q) } : s)
  }

  function updateQuestionScope(id: string, scope: QuestionScope) {
    setRecSettings(s => s ? { ...s, questions: s.questions.map(q => q.id === id ? { ...q, scope } : q) } : s)
  }

  function onDragStart(idx: number) { setDragging(idx) }
  function onDragOver(e: React.DragEvent, idx: number) { e.preventDefault(); setDragOver(idx) }
  function onDrop(idx: number) {
    if (dragging === null || !recSettings) return
    const qs = [...recSettings.questions].sort((a, b) => a.order - b.order)
    const [moved] = qs.splice(dragging, 1)
    qs.splice(idx, 0, moved)
    setRecSettings(s => s ? { ...s, questions: qs.map((q, i) => ({ ...q, order: i + 1 })) } : s)
    setDragging(null); setDragOver(null)
  }

  function updateStrength(key: 'strong' | 'medium' | 'weak', field: 'min' | 'label', value: string) {
    setRecSettings(s => s ? {
      ...s,
      strengthRating: { ...s.strengthRating, [key]: { ...s.strengthRating[key], [field]: field === 'min' ? Number(value) : value } }
    } : s)
  }

  function updateHiveType(id: string, field: 'label' | 'frames', value: string) {
    setRecSettings(s => s ? {
      ...s,
      hiveTypes: s.hiveTypes.map(h => h.id === id ? { ...h, [field]: field === 'frames' ? Number(value) : value } : h)
    } : s)
  }

  const sortedQuestions = recSettings ? [...recSettings.questions].sort((a, b) => a.order - b.order) : []

  return (
    <Container>
      <PageTitle>{t('settings.title')}</PageTitle>

      <TabsRow>
        <TabBtn active={activeTab === 'general'} onClick={() => setActiveTab('general')}>
          ⚙️ {t('settings.general')}
        </TabBtn>
        <TabBtn active={activeTab === 'recording'} onClick={() => setActiveTab('recording')}>
          <MicIcon style={{ fontSize: 16 }} /> {t('settings.recordingSettings')}
        </TabBtn>
      </TabsRow>

      {/* ── GENERAL TAB ── */}
      {activeTab === 'general' && (
        <>
          {/* Account */}
          <SectionHeader>
            <AccountCircleIcon sx={{ color: '#6B4A2B', fontSize: 22 }} />
            <SectionTitle>{t('settings.account')}</SectionTitle>
          </SectionHeader>
          <StyledCard>
            <CardContent>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>{t('users.name')}</InfoLabel>
                  <InfoValue>{user?.name || '—'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>{t('users.email')}</InfoLabel>
                  <InfoValue>{user?.email || '—'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>{t('users.role')}</InfoLabel>
                  <Chip label={user?.role ? t(`users.roles.${user.role}`, { defaultValue: user.role }) : '—'} size="small"
                    sx={{ backgroundColor: '#FFF3D0', color: '#D18A00', fontWeight: 600, textTransform: 'capitalize' }} />
                </InfoItem>
              </InfoGrid>
            </CardContent>
          </StyledCard>

          {/* Language */}
          <SectionHeader>
            <LanguageIcon sx={{ color: '#6B4A2B', fontSize: 22 }} />
            <SectionTitle>{t('settings.language')}</SectionTitle>
          </SectionHeader>
          <StyledCard>
            <CardContent>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {t('settings.languageDescription')}
              </Typography>
              <RadioGroup value={language} onChange={(e) => setLanguage(e.target.value as Language)}>
                <FormControlLabel value="en" control={<Radio sx={{ '&.Mui-checked': { color: '#F6B400' } }} />}
                  label={<LangLabel><span>🇺🇸</span><span>English</span></LangLabel>} />
                <FormControlLabel value="es" control={<Radio sx={{ '&.Mui-checked': { color: '#F6B400' } }} />}
                  label={<LangLabel><span>🇪🇸</span><span>Español</span></LangLabel>} />
                <FormControlLabel value="he" control={<Radio sx={{ '&.Mui-checked': { color: '#F6B400' } }} />}
                  label={<LangLabel><span>🇮🇱</span><span>עברית</span></LangLabel>} />
                <FormControlLabel value="ar" control={<Radio sx={{ '&.Mui-checked': { color: '#F6B400' } }} />}
                  label={<LangLabel><span>🇸🇦</span><span>العربية</span></LangLabel>} />
                <FormControlLabel value="th" control={<Radio sx={{ '&.Mui-checked': { color: '#F6B400' } }} />}
                  label={<LangLabel><span>🇹🇭</span><span>ภาษาไทย</span></LangLabel>} />
              </RadioGroup>
            </CardContent>
          </StyledCard>

          {/* Notifications */}
          <SectionHeader>
            <NotificationsIcon sx={{ color: '#6B4A2B', fontSize: 22 }} />
            <SectionTitle>{t('settings.notifications')}</SectionTitle>
          </SectionHeader>
          <StyledCard>
            <CardContent>
              <SettingRow>
                <div>
                  <Typography variant="body1" fontWeight={500}>{t('settings.notifications')}</Typography>
                  <Typography variant="body2" color="textSecondary">{t('settings.notificationsDescription')}</Typography>
                </div>
                <Switch checked={notificationsEnabled} onChange={toggleNotifications}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F6B400' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#F6B400' } }} />
              </SettingRow>
            </CardContent>
          </StyledCard>

          {/* Units */}
          <SectionHeader>
            <StraightenIcon sx={{ color: '#6B4A2B', fontSize: 22 }} />
            <SectionTitle>{t('settings.units')}</SectionTitle>
          </SectionHeader>
          <StyledCard>
            <CardContent>
              <SettingRow>
                <Typography variant="body1" fontWeight={500}>{t('settings.temperature')}</Typography>
                <ToggleButtonGroup value={temperatureUnit} exclusive
                  onChange={(_, v) => v && setTemperatureUnit(v)} size="small">
                  <ToggleButton value="celsius" sx={{ '&.Mui-selected': { backgroundColor: '#F6B400', color: '#fff' } }}>°C</ToggleButton>
                  <ToggleButton value="fahrenheit" sx={{ '&.Mui-selected': { backgroundColor: '#F6B400', color: '#fff' } }}>°F</ToggleButton>
                </ToggleButtonGroup>
              </SettingRow>
              <Divider sx={{ my: 2 }} />
              <SettingRow>
                <Typography variant="body1" fontWeight={500}>{t('settings.distance')}</Typography>
                <ToggleButtonGroup value={distanceUnit} exclusive
                  onChange={(_, v) => v && setDistanceUnit(v)} size="small">
                  <ToggleButton value="km" sx={{ '&.Mui-selected': { backgroundColor: '#F6B400', color: '#fff' } }}>{t('settings.kmShort')}</ToggleButton>
                  <ToggleButton value="miles" sx={{ '&.Mui-selected': { backgroundColor: '#F6B400', color: '#fff' } }}>{t('settings.miShort')}</ToggleButton>
                </ToggleButtonGroup>
              </SettingRow>
            </CardContent>
          </StyledCard>

          {/* About */}
          <SectionHeader>
            <InfoIcon sx={{ color: '#6B4A2B', fontSize: 22 }} />
            <SectionTitle>{t('settings.about')}</SectionTitle>
          </SectionHeader>
          <StyledCard>
            <CardContent>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>{t('settingsScreen.appName')}</InfoLabel>
                  <InfoValue>v1.2.0</InfoValue>
                </InfoItem>
              </InfoGrid>
            </CardContent>
          </StyledCard>
        </>
      )}

      {/* ── RECORDING TAB ── */}
      {activeTab === 'recording' && (
        <RecSection>
          {recLoading ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>{t('common.loading')}</p>
          ) : recSettings ? (
            <>
              {/* Recording mode — decides whether the assistant drives the visit or the beekeeper does */}
              <RecCard>
                <RecCardTitle style={{ marginTop: 0 }}>🎚️ {t('settings.recMode')}</RecCardTitle>
                {(['questions', 'free'] as RecordingMode[]).map(m => {
                  const selected = (recSettings.mode || 'questions') === m
                  return (
                    <label
                      key={m}
                      style={{
                        display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
                        padding: 12, marginBottom: 8, borderRadius: 8,
                        border: selected ? '2px solid #6366f1' : '1px solid #e5e7eb',
                        background: selected ? 'rgba(99,102,241,0.06)' : 'transparent',
                      }}
                    >
                      <input
                        type="radio"
                        name="recording-mode"
                        checked={selected}
                        onChange={() => setRecSettings(s => (s ? { ...s, mode: m } : s))}
                        style={{ marginTop: 3 }}
                      />
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          {t(m === 'questions' ? 'settings.recModeGuided' : 'settings.recModeFree')}
                        </span>
                        <span style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                          {t(m === 'questions' ? 'settings.recModeGuidedHint' : 'settings.recModeFreeHint')}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </RecCard>

              {/* Voice */}
              <RecCard>
                <RecCardTitle style={{ marginTop: 0 }}>🔊 {t('settings.voice')}</RecCardTitle>
                <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>{t('settings.voiceHint')}</p>
                <select
                  value={recSettings.voice || 'marin'}
                  onChange={e => setRecSettings(s => (s ? { ...s, voice: e.target.value } : s))}
                  style={{
                    padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb',
                    fontSize: 14, color: '#374151', background: '#fff', cursor: 'pointer',
                  }}
                >
                  {VOICES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </RecCard>

              {/* Questions */}
              <RecCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                  <RecCardTitle style={{ margin: 0 }}>🎙️ {t('settings.recQuestions')}</RecCardTitle>
                  <Btn variant="ghost" style={{ fontSize: 12, padding: '6px 12px' }} onClick={loadPromptPreview}>
                    👁 {t('settings.aiPrompt')}
                  </Btn>
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
                  {t('settings.recHint')}
                </p>
                {translatingRec && (
                  <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>🌐 …</p>
                )}
                {recTranslation && !recTranslation.sameLanguage && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 600 }}>
                      🌐 {t('settings.translatedFrom', { lang: t(`settings.${LANG_NAME_KEY[recTranslation.sourceLanguage] || 'english'}`) })}
                    </span>
                    <Btn variant="ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setShowOriginal(o => !o)}>
                      {showOriginal ? t('recommendations.showTranslation') : t('recommendations.showOriginal')}
                    </Btn>
                  </div>
                )}

                {sortedQuestions.map((q, idx) => (
                  <QuestionRow
                    key={q.id}
                    enabled={q.enabled}
                    draggable
                    onDragStart={() => onDragStart(idx)}
                    onDragOver={e => onDragOver(e, idx)}
                    onDrop={() => onDrop(idx)}
                    style={{ border: dragOver === idx ? '2px solid #6366f1' : undefined }}
                  >
                    <DragHandle>⠿</DragHandle>
                    <Toggle>
                      <input type="checkbox" checked={q.enabled} disabled={q.required} onChange={() => toggleQuestion(q.id)} />
                      <span />
                    </Toggle>
                    <QInfo>
                      <QLabel>
                        {q.required && <span style={{ color: '#f59e0b' }}>⭐</span>}
                        {trQuestion(q.id)?.label || q.label}
                        <QType>{TypeIcon[q.type]} {t(`settings.questionType.${q.type}`, { defaultValue: q.type })}</QType>
                        {/* Site answers are asked once a visit; hive answers repeat per hive. */}
                        <select
                          value={q.scope || 'hive'}
                          onChange={e => updateQuestionScope(q.id, e.target.value as QuestionScope)}
                          title={t('settings.scopeHint')}
                          style={{
                            fontSize: 11, padding: '2px 6px', borderRadius: 6,
                            border: '1px solid #e5e7eb', color: '#6b7280', background: '#fff',
                            marginInlineStart: 6, cursor: 'pointer',
                          }}
                        >
                          <option value="hive">🐝 {t('settings.scopeHive')}</option>
                          <option value="yard">📍 {t('settings.scopeYard')}</option>
                        </select>
                      </QLabel>
                      {isTranslated
                        ? <QPromptText>{trQuestion(q.id)?.prompt || q.prompt}</QPromptText>
                        : (q.enabled
                          ? <QPromptInput value={q.prompt} onChange={e => updateQuestionPrompt(q.id, e.target.value)} />
                          : <QPromptText>{q.prompt}</QPromptText>)
                      }
                    </QInfo>
                  </QuestionRow>
                ))}

                {showPrompt && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{t('settings.aiSystemPrompt')}</span>
                      <Btn variant="ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setShowPrompt(false)}>{t('settings.close')}</Btn>
                    </div>
                    <PromptPreview>{prompt}</PromptPreview>
                  </div>
                )}
              </RecCard>

              {/* Hive Types */}
              <RecCard>
                <RecCardTitle>🪣 {t('settings.hiveTypes')}</RecCardTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {recSettings.hiveTypes.map(h => (
                    <div key={h.id} style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
                      <SmLabel>{t('settings.nameField')}</SmLabel>
                      <SmInput value={h.label} onChange={e => updateHiveType(h.id, 'label', e.target.value)} />
                      <SmLabel>{t('settings.framesCount')}</SmLabel>
                      <SmInput type="number" value={h.frames} onChange={e => updateHiveType(h.id, 'frames', e.target.value)} />
                    </div>
                  ))}
                </div>
              </RecCard>

              {/* Strength Rating */}
              <RecCard>
                <RecCardTitle>💪 {t('settings.strengthRating')}</RecCardTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {(['strong', 'medium', 'weak'] as const).map(key => {
                    const r = recSettings.strengthRating[key]
                    return (
                      <div key={key} style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                          <ColorDot color={r.color} />
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{r.label}</span>
                        </div>
                        <SmLabel>{t('settings.nameField')}</SmLabel>
                        <SmInput value={r.label} onChange={e => updateStrength(key, 'label', e.target.value)} />
                        <SmLabel>{t('settings.minFrames')}</SmLabel>
                        <SmInput type="number" value={r.min} onChange={e => updateStrength(key, 'min', e.target.value)} />
                      </div>
                    )
                  })}
                </div>
                <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                  {t('settings.strong')}: {recSettings.strengthRating.strong.min}+ &nbsp;|&nbsp;
                  {t('settings.medium')}: {recSettings.strengthRating.medium.min}–{recSettings.strengthRating.strong.min - 1} &nbsp;|&nbsp;
                  {t('settings.weak')}: 0–{recSettings.strengthRating.medium.min - 1}
                </p>
              </RecCard>

              <SaveBar>
                {recSaved && <span style={{ color: '#10b981', fontWeight: 600, alignSelf: 'center', fontSize: 13 }}>✅ {t('settings.saved')}</span>}
                <Btn variant="ghost" onClick={loadRecSettings}>{t('settings.cancelChanges')}</Btn>
                <Btn onClick={saveRecSettings} disabled={recSaving}>{recSaving ? t('settings.saving') : t('settings.saveSettings')}</Btn>
              </SaveBar>
            </>
          ) : (
            <p style={{ color: '#ef4444' }}>{t('settings.loadError')}</p>
          )}
        </RecSection>
      )}
    </Container>
  )
}

// ─── Original Styled Components ───────────────────────────────────────────────

const Container = styled.div`
  padding: ${props => props.theme.spacing.lg};
  max-width: 900px;
`

const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize.xxlarge};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
`

const SectionHeader = styled.div`
  display: flex; align-items: center; gap: 8px;
  margin: ${props => props.theme.spacing.md} 0 ${props => props.theme.spacing.xs} 0;
`

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.medium};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`

const StyledCard = styled(Card)`
  margin-bottom: ${props => props.theme.spacing.sm};
  border-radius: 16px !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
`

const InfoGrid = styled.div`display: flex; flex-wrap: wrap; gap: 24px;`
const InfoItem = styled.div`display: flex; flex-direction: column; gap: 4px;`

const InfoLabel = styled.span`
  font-size: 13px; color: ${props => props.theme.colors.textSecondary};
`
const InfoValue = styled.span`
  font-size: 15px; font-weight: 600; color: ${props => props.theme.colors.text};
`

const SettingRow = styled.div`display: flex; justify-content: space-between; align-items: center;`

const LangLabel = styled.span`display: flex; align-items: center; gap: 10px; font-size: 15px;`

export default SettingsScreen
